import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, AlertTriangle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryState, useQueryStates, parseAsInteger, parseAsString, parseAsArrayOf, createParser } from 'nuqs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { VoucherResponse } from '@/app/types/voucher';
import { useOrganization } from '@/app/hooks/useOrganization';
import { listVouchers, cancelVoucher } from '@/app/services/voucherService';
import { PaginatedResponse } from '@/app/types/api';
import { SortingState } from '@tanstack/react-table';
import { ExpenseDataTable } from './ExpenseDataTable';
import { expenseColumns } from './columns';
import { format } from 'date-fns';
import { useHotkeys } from 'react-hotkeys-hook';

// Define the relevant voucher type codes/IDs for expenses
// TODO: Fetch these dynamically or from config instead of hardcoding
const EXPENSE_VOUCHER_TYPES = [3]; // Example: PINV=2, PMT=3, JV=7 (Adjust based on your actual IDs/Codes)

// Define the sort direction type
type SortDirection = 'asc' | 'desc';

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return [{ id: 'transaction_date', desc: true }]
    const [field, direction] = value.split('-')
    return [{
      id: field || 'transaction_date',
      desc: direction === 'desc'
    }]
  },
  serialize: (value: SortingState) => {
    if (!value.length) return 'transaction_date-desc'
    const sort = value[0]
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`
  }
})

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10)
    return isNaN(page) || page < 1 ? 0 : page - 1 // Convert 1-based to 0-based
  },
  serialize: (value: number) => {
    return String(value + 1) // Convert 0-based to 1-based
  }
})

interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number;
  pageCount?: number;
  total_pages?: number;
  totalPages?: number;
}

// Filter configuration object for consistent filtering approach
const FILTER_CONFIG = {
  // Filter format to use: 'array' (with field/value/op objects) or 'object' (simple key-value pairs)
  format: 'array',
  
  // Special field configurations
  fields: {
    'voucher_number': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%`
    },
    'reference_number': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%`
    },
    'site.name': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%`
    },
    'is_cancelled': {
      // Map front-end filter values to backend format
      specialCase: (value: string): { field: string; value: boolean; op: string } | null => {
        if (value === 'active') {
          return { field: 'is_cancelled', value: false, op: 'eq' };
        } else if (value === 'cancelled') {
          return { field: 'is_cancelled', value: true, op: 'eq' };
        }
        return null;
      }
    },
    'total_amount': {
      op: 'eq' // Exact match for amounts
    }
  }
};

// Define filter object types
interface FilterObject {
  field: string;
  value: string | number | boolean | string[];
  op: string;
}

// Helper function to build filter parameters based on config
const buildFilterParams = (filters: Record<string, string | number | boolean | { from?: string; to?: string }>, config = FILTER_CONFIG): string => {
  console.log('buildFilterParams called with filters:', filters);
  
  if (config.format === 'array') {
    // Build array of filter objects with field, value, op
    const filterArray: (FilterObject | FilterObject[] | null)[] = Object.entries(filters).map(([field, value]) => {
      // Skip empty values
      if (value === undefined || value === null || value === '') {
        return null;
      }
      
      // Handle date range filters
      if (field === 'transaction_date' && typeof value === 'object' && 'from' in value) {
        const dateFilter = value as { from?: string; to?: string };
        const dateFilters: FilterObject[] = [];
        
        console.log('Processing date filter:', dateFilter);
        
        if (dateFilter.from) {
          // Ensure we add time component for start of day
          const fromDate = `${dateFilter.from} 00:00:00`;
          dateFilters.push({
            field: 'transaction_date',
            value: fromDate,
            op: 'gte' // Greater than or equal
          });
        }
        
        if (dateFilter.to) {
          // Ensure we add time component for end of day
          const toDate = `${dateFilter.to} 23:59:59`;
          dateFilters.push({
            field: 'transaction_date',
            value: toDate,
            op: 'lte' // Less than or equal
          });
        }
        
        console.log('Generated date filters:', dateFilters);
        return dateFilters.length > 0 ? dateFilters : null;
      }
      
      // Check for special case handling
      const fieldConfig = config.fields[field];
      if (fieldConfig?.specialCase) {
        const specialCase = fieldConfig.specialCase(String(value));
        if (specialCase) {
          return specialCase as FilterObject;
        }
      }
      
      // Get actual field name from config or use the original
      const actualField = fieldConfig?.field || field;
      
      // Transform value if needed
      const transformedValue = fieldConfig?.transform ? 
        fieldConfig.transform(String(value)) : 
        value;
      
      // Use configured operator or default to 'eq'
      const op = fieldConfig?.op || 'eq';
      
      return {
        field: actualField,
        value: transformedValue,
        op // Use 'op' instead of 'operator'
      } as FilterObject;
    });
    
    // Flatten the array (to handle date range filters that return arrays)
    const flattenedArray = filterArray.flatMap(f => f || []).filter(Boolean);
    
    const result = JSON.stringify(flattenedArray);
    console.log('Final filter JSON:', result);
    return result;
  } else {
    // Simple object format for exact matches
    const filterObj: Record<string, string | number | boolean> = {};
    
    Object.entries(filters).forEach(([field, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Apply field mapping if configured
        const actualField = config.fields[field]?.field || field;
        filterObj[actualField] = value;
      }
    });
    
    return JSON.stringify(filterObj);
  }
};

const ExpenseListPage: React.FC = (): JSX.Element => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const navigate = useNavigate();

  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0))
  const [sorting, setSorting] = useQueryState('sortBy', sortingParser.withDefault([{ id: 'transaction_date', desc: true }]))
  
  // Use nuqs for filter state management
  const [filterStates, setFilterStates] = useQueryStates({
    voucher_number: parseAsString,
    reference_number: parseAsString,
    site_name: parseAsString, // Changed from 'site.name' to avoid URL encoding issues
    is_cancelled: parseAsString,
    transaction_date_from: parseAsString,
    transaction_date_to: parseAsString,
    total_amount: parseAsString,
  }, {
    // Use shallow routing to prevent full page reloads
    shallow: true,
  })

  // Build filters object from nuqs state
  const filters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }> = {}
  
  // Add simple filters
  if (filterStates.voucher_number) filters.voucher_number = filterStates.voucher_number
  if (filterStates.reference_number) filters.reference_number = filterStates.reference_number
  if (filterStates.site_name) filters['site.name'] = filterStates.site_name // Map site_name to site.name
  if (filterStates.is_cancelled) filters.is_cancelled = filterStates.is_cancelled
  if (filterStates.total_amount) filters.total_amount = filterStates.total_amount
  
  // Add date range filters
  if (filterStates.transaction_date_from || filterStates.transaction_date_to) {
    filters.transaction_date = {
      from: filterStates.transaction_date_from || undefined,
      to: filterStates.transaction_date_to || undefined
    }
  }

  const [limit] = useState<number>(15); // Adjust page size if needed

  // Hotkey for creating new expense (e.g., Alt+N or Ctrl+N)
  useHotkeys('alt+n, ctrl+n', () => navigate('/expenses/detail'), { preventDefault: true });



  // Determine sort parameters for API call
  const getSortParams = useCallback(() => {
    if (sorting.length === 0) return { sort_by: 'transaction_date', sort_dir: 'desc' as SortDirection };
    const sort = sorting[0];
    return { sort_by: sort.id, sort_dir: (sort.desc ? 'desc' : 'asc') as SortDirection };
  }, [sorting]);

  // Fetch expenses (vouchers of specific types)
  const { data: expensesData, isLoading, isError, error } = useQuery<ExtendedPaginatedResponse<VoucherResponse>, Error>({
    queryKey: ['expenses', page, limit, sorting, filters, currentOrganizationId],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams();
      const skip = page * limit;
      
      // Build base filters for expenses
      const baseFilters: FilterObject[] = [
        { field: 'voucher_type_id', op: 'in', value: EXPENSE_VOUCHER_TYPES },
        { field: 'party_id', op: 'isnull', value: true } // Expenses should not have a party
      ];
      
      // Convert filters object to array format and combine with base filters
      const filterParams = buildFilterParams(filters);
      const parsedFilters = filterParams ? JSON.parse(filterParams) : [];
      const allFilters = [...baseFilters, ...parsedFilters];
      
      console.log('Fetching expenses with filters:', allFilters);
      
      return listVouchers({
        skip,
        limit,
        sort_by,
        sort_dir,
        filters: JSON.stringify(allFilters),
      });
    },
    enabled: !!currentOrganizationId,
  });

  const pageCount = expensesData?.page_count ?? expensesData?.pageCount ?? expensesData?.total_pages ?? expensesData?.totalPages ?? (expensesData ? Math.ceil(expensesData.total / limit) : 0);

  // Cancel Expense Mutation (assuming cancellation reverses accounting/stock)
   const cancelExpenseMutation = useMutation({
    mutationFn: (id: number) => cancelVoucher(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] }); // Also invalidate generic vouchers
      toast.success('Expense cancelled successfully');
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to cancel";
      toast.error(`Failed to cancel expense: ${errorMsg}`);
    },
  });


  // Event handlers
  const handleSortingChange = useCallback((newSorting: SortingState): void => {
    setSorting(newSorting);
    setPage(0);
  }, [setSorting, setPage]);

  const handlePageChange = useCallback((newPage: number): void => {
    setPage(newPage);
  }, [setPage]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Record<string, unknown>): void => {
    console.log('handleFilterChange called with:', newFilters);
    
    // Build the new filter states for nuqs
    const updates: Record<string, string | string[] | null> = {}
    let hasActualChanges = false

    // List of all possible filter keys that could come from the DataTable
    const knownFilterKeys = ['voucher_number', 'reference_number', 'site.name', 'is_cancelled', 'transaction_date', 'total_amount']
    
    // First, check all known keys and set to null if not present in newFilters
    knownFilterKeys.forEach(key => {
      if (!(key in newFilters)) {
        const mappedKey = key === 'site.name' ? 'site_name' : key
        
        // For date range filters, clear both from and to
        if (key === 'transaction_date') {
          if (filterStates[`${key}_from`] !== null || filterStates[`${key}_to`] !== null) {
            hasActualChanges = true
            updates[`${key}_from`] = null
            updates[`${key}_to`] = null
          }
        } else if (filterStates[mappedKey] !== null) {
          hasActualChanges = true
          updates[mappedKey] = null
        }
      }
    })

    Object.entries(newFilters).forEach(([key, value]) => {
      // Map 'site.name' to 'site_name' for URL compatibility
      const mappedKey = key === 'site.name' ? 'site_name' : key
      
      // Handle date range object
      if (typeof value === 'object' && value !== null && 'from' in value) {
        const dateRange = value as { from?: string; to?: string }
        const fromKey = `${key}_from`
        const toKey = `${key}_to`
        const newFrom = dateRange.from || null
        const newTo = dateRange.to || null
        
        // Check if values actually changed
        if (filterStates[fromKey] !== newFrom || filterStates[toKey] !== newTo) {
          hasActualChanges = true
        }
        
        updates[fromKey] = newFrom
        updates[toKey] = newTo
      } else if (value === undefined || value === null || value === '') {
        // Clear the filter
        if (filterStates[mappedKey] !== null) {
          hasActualChanges = true
        }
        updates[mappedKey] = null
      } else {
        // Handle simple string/number values
        const newValue = String(value)
        if (filterStates[mappedKey] !== newValue) {
          hasActualChanges = true
        }
        updates[mappedKey] = newValue
      }
    })

    console.log('Updating filters with:', updates, 'hasActualChanges:', hasActualChanges)
    console.log('Current filterStates:', filterStates)
    
    // Update filters using nuqs
    setFilterStates(updates)
    
    // Only reset to first page when filters actually change
    if (hasActualChanges) {
      setPage(0)
    }
  }, [setFilterStates, setPage, filterStates]);

  // Example handler for cancelling (can be triggered from DataTable actions)
  const handleCancel = (expenseId: number): void => {
    if(confirm(`Are you sure you want to cancel expense voucher ID ${expenseId}? This may reverse accounting entries.`)) {
        cancelExpenseMutation.mutate(expenseId);
    }
  };
  // Handler for viewing expense details
  const handleView = (expenseId: number): void => {
    navigate(`/expenses/detail/${expenseId}`);
  };
   
  // Handler for editing (not yet implemented)
  const handleEdit = (): void => {
    toast.info("Edit functionality for expenses is not yet implemented.");
  };


  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Expenses</h1>
        <Button asChild>
          <Link to="/expenses/detail"> {/* Link to create form */}
            <Plus className="h-4 w-4 mr-2" />
            Record Expense
          </Link>
        </Button>
      </div>


      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading expenses: {(error as Error).message}
          </AlertDescription>
        </Alert>
      )}

      {/* Expense Data Table */}
      <ExpenseDataTable
        data={expensesData?.items || []}
        columns={expenseColumns({ onEdit: handleEdit, onCancel: handleCancel, onView: handleView })}
        isLoading={isLoading || cancelExpenseMutation.isPending}
        pageCount={pageCount}
        page={page}
        setPage={handlePageChange}
        limit={limit}
        totalRecords={expensesData?.total || 0}
        onSortingChange={handleSortingChange}
        sortingState={sorting}
        onFilterChange={handleFilterChange}
        initialFilters={filters}
      />

      {/* Add Confirmation Dialog for Cancellation if needed */}

    </div>
  );
};

export default ExpenseListPage; 